import React from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import Alert from 'react-s-alert';
import { SessionScreen } from './commonComponents/SessionScreen';
import { Spinner } from './commonComponents/Spinner';
import Login from './mainComponents/Login';
import MapContainer from './mainComponents/MapContainer';
import { DentistList } from './mainComponents/UsersList/DentistList/DentistList';
import { DSOOfficeList } from './mainComponents/UsersList/DSOOfficeList/DSOOfficeList';
import { UnfinishedUserList } from './mainComponents/UsersList/UnfinishedUserList/UnfinishedUserList';
import { ProfessionalList } from './mainComponents/UsersList/ProfessionalList/ProfessionalList';
import { Licenses } from './mainComponents/Licenses/Licenses';
import PostPPJob from './mainComponents/Job/PP/PostPPJob';

import { JobList } from './mainComponents/JobList/JobList';

import { AppShell } from './commonComponents/AppShell';
import { BestInDental } from './mainComponents/BestInDental';
import { CycleDetail } from './mainComponents/BestInDental/CycleDetail';
import { GotuAdmins } from './mainComponents/GotuAdmins';
import { LocationUserProfile, LocationUsers } from './mainComponents/LocationUsers';
import { SalesMetros } from './mainComponents/SalesMetros';
import { PromoCodeDetail, PromoCodes } from './mainComponents/PromoCodes';
import Profile from './mainComponents/User/Profile';
import JobDetails from './mainComponents/Job/JobDetails';
import JobCreateComponent from './mainComponents/Job/JobCreateComponent';
import SMSCampaign from './mainComponents/SMSCampaign';
import { ChallengesPage, PromotionsPage } from './mainComponents/ProPrograms';
import TableauBoard from './mainComponents/TableauBoard';

import history from '../history';

// Bootstrap's stylesheet is imported in src/index.js so the global cascade
// order (bootstrap -> design system -> compat) lives in one place.
import PPJobDetails from './mainComponents/Job/PP/PPJobDetails';
import ChatMessages from './mainComponents/ChatMessages';
import { MultidayList } from './mainComponents/MultidayList';
import { MultidayJobs } from './mainComponents/Job/Multiday/MultidayJobs';
import PostMultiday from './mainComponents/Job/Multiday/PostMultiday';
import { ReferralList } from './mainComponents/ReferralList/ReferralList';
import SSOCallbackHandler from './mainComponents/SSOCallbackHandler';
import { SessionUnreachable } from './mainComponents/SessionUnreachable';
import { SESSION_STATUS, retrySession, useSession } from '../domain/Auth';
import { useAdminUserBootstrap } from '../hooks/useAdminUserBootstrap';
import { useAnalyticsIdentity } from '../hooks/useAnalyticsIdentity';
import { useLogoutRedirect } from '../hooks/useLogoutRedirect';
import { useSessionTeardown } from '../hooks/useSessionTeardown';
import {
  getShouldEnableBestInDental,
  getShouldEnableGotuAdmins,
  getShouldEnableLocationUsers,
  getShouldEnableSalesMetrosPage,
} from '../posthog';
import { PermanentJobList } from './mainComponents/PermanentJobList/PermanentJobList';
import { DsoControls } from './mainComponents/DsoControls';
import { PermanentJobListV2 } from './mainComponents/PermanentJobV2/PermanentJobListV2';
import { PostPermanentJobV2 } from './mainComponents/PermanentJobV2/PostPermanentJobV2';
import { PermanentJobDetailV2 } from './mainComponents/PermanentJobV2/PermanentJobDetailV2';
import { CreateOnsiteInterview } from './mainComponents/PermanentJobV2/CreateOnsiteInterview';
import { OnsiteInterviewList } from './mainComponents/PermanentJobV2/OnsiteInterviewList';

const homePath = process.env.PUBLIC_URL;
const tableauBoardGraph =
  'https://prod-useast-a.online.tableau.com/t/tempmeecom/views/DashboardData/Dashboard1/6bf5bb7a-5b04-456b-9d84-e1fb49f59477/0d859810-89b3-4506-86c6-3eeb724ffb0e';
const tableauBoardList =
  'https://prod-useast-a.online.tableau.com/t/tempmeecom/views/Dashboard3_0/Sheet36';

const BootScreen = () => (
  <SessionScreen bare>
    <Spinner />
  </SessionScreen>
);

/**
 * Signed out: the login screen everywhere, and the Google SSO hand-off on
 * `/auth/google`, which signs in and moves on to `/` itself.
 */
const SignedOutApp = () => (
  <Router basename={homePath} history={history}>
    <Route path="/" component={Login} />
    <Route path="/auth/google" component={SSOCallbackHandler} />
    <div>
      <Alert stack={{ limit: 3 }} />
    </div>
  </Router>
);

const SignedInApp = () => (
  <Router basename={homePath} history={history}>
    <AppShell>
      <Switch>
        <Route path="/dentist" component={DentistList} />
        <Route path="/dso" component={DSOOfficeList} />
        <Route path="/hygienist" component={ProfessionalList} />
        <Route path="/licenses" component={Licenses} />
        <Route path="/unfinished" component={UnfinishedUserList} />
        <Route path="/dso-controls" component={DsoControls} />
        <Route path="/jobs" component={JobList} />
        <Route path="/pp-job/:id" component={PPJobDetails} />
        <Route path="/pp-jobs" exact component={PermanentJobList} />
        <Route path="/permanent-jobs" exact component={PermanentJobListV2} />
        <Route path="/interviews" exact component={OnsiteInterviewList} />
        <Route path="/permanent-job/post" component={PostPermanentJobV2} />
        <Route
          path="/permanent-job/job/:jobId/create-onsite-interview"
          component={CreateOnsiteInterview}
        />
        <Route
          path="/permanent-job/office/:officeId/job/:jobId/:currentTab"
          component={PermanentJobDetailV2}
        />
        <Route path="/permanent-job/office/:officeId/job/:jobId" component={PermanentJobDetailV2} />
        <Route path="/user/:profileType/:id/:currentTab" component={Profile} />
        <Route path="/user/:profileType/:id" component={Profile} />
        <Route path="/job/:id/:currentTab" component={JobDetails} />
        <Route path="/job/:id" component={JobDetails} />
        <Route path="/pp-post-job" component={PostPPJob} />
        <Route path="/jobcreate" component={JobCreateComponent} />
        <Route path="/sms" component={SMSCampaign} />
        <Route path="/pro-programs/promotions" component={PromotionsPage} />
        <Route path="/pro-programs/challenges" component={ChallengesPage} />
        {/* Programs → Promo Codes (GOTU-5661). List first with `exact`, the
                detail second without it — Switch takes the first match. */}
        <Route path="/promo-codes" exact component={PromoCodes} />
        <Route path="/promo-codes/:campaignId" component={PromoCodeDetail} />
        {/* Bare /pro-programs was the single tabbed page before the split;
                keep it working by landing on the tab that used to be default. */}
        <Route
          path="/pro-programs"
          exact
          render={() => <Redirect to="/pro-programs/promotions" />}
        />
        {/* /properks predates Pro Programs — it was the original standalone
                challenges page and was never linked from the UI. Kept as a
                redirect so any bookmark still resolves. */}
        <Route path="/properks" render={() => <Redirect to="/pro-programs/challenges" />} />
        {/* The detail route is registered second and without `exact`, so
                /best-in-dental matches the list and /best-in-dental/:id the
                cycle. Switch takes the first match, so order is load-bearing.
                Both sit behind the same flag as the rail entry — a menu row
                pointing at an unregistered route silently redirects to `/`.

                Gate each Route on its own — never wrap them in a fragment.
                Switch reads `path` off its direct children; a fragment has
                none, so Switch treats it as an unconditional match and stops
                there, stranding every route below (home included) whenever the
                flag is on. */}
        {getShouldEnableBestInDental() && (
          <Route path="/best-in-dental" exact component={BestInDental} />
        )}
        {getShouldEnableBestInDental() && (
          <Route path="/best-in-dental/:cycleId" component={CycleDetail} />
        )}
        {/* Same flag as the rail entry, and gated on its own Route for the
                same reason as the two above — Switch reads `path` off its
                direct children. */}
        {getShouldEnableGotuAdmins() && <Route path="/gotu-admins" exact component={GotuAdmins} />}
        {getShouldEnableSalesMetrosPage() && (
          <Route path="/sales-metros" exact component={SalesMetros} />
        )}
        {/* Users → Location Users (GOTU-19601) and one Location User
                (GOTU-19602): the list `exact`, then the profile, its tab route
                first as the office profile's is, since Switch takes the first
                match. One flag for all three and the rail entry, each gated on
                its own Route for the reason given above. */}
        {getShouldEnableLocationUsers() && (
          <Route path="/location-users" exact component={LocationUsers} />
        )}
        {getShouldEnableLocationUsers() && (
          <Route path="/location-users/:identityId/:tab" component={LocationUserProfile} />
        )}
        {getShouldEnableLocationUsers() && (
          <Route path="/location-users/:identityId" component={LocationUserProfile} />
        )}
        <Route path="/messages" exact component={ChatMessages} />
        <Route path="/messages/:id" component={(props) => <ChatMessages {...props} />} />
        <Route path="/multiday/post" exact component={PostMultiday} />
        <Route path="/multiday/:multidayId" component={MultidayJobs} />
        <Route path="/multiday" exact component={MultidayList} />
        <Route path="/referral" component={ReferralList} />
        <Route
          path="/dash-metrics"
          exact
          component={() => <TableauBoard url={tableauBoardGraph} />}
        />
        <Route path="/dash-list" exact component={() => <TableauBoard url={tableauBoardList} />} />
        <Route path="/" exact component={MapContainer} />
        <Route path="*">
          <Redirect to="/" />
        </Route>
      </Switch>
    </AppShell>
    <div>
      <Alert stack={{ limit: 3 }} />
    </div>
  </Router>
);

/**
 * The app renders from the session's status and nothing else: restoring a
 * stored session, the server unreachable while doing so, signed out, signed in.
 * Ending a session — however it ends — clears everything the session left
 * behind (`useSessionTeardown`); logging out also sends the browser home
 * (`useLogoutRedirect`).
 */
const App = () => {
  // Order matters: `useSession` starts the session in its effect, and a session
  // stored by another build ends synchronously inside that start. PostHog must
  // be initialised (a reset before that does nothing) and the session-end
  // listeners registered before it runs.
  useAnalyticsIdentity();
  useSessionTeardown();
  useLogoutRedirect();
  const { status } = useSession();
  const isSignedIn = status === SESSION_STATUS.SIGNED_IN;
  const { isLoading: isLoadingAdmin } = useAdminUserBootstrap(isSignedIn);

  if (status === SESSION_STATUS.UNREACHABLE) return <SessionUnreachable onRetry={retrySession} />;
  if (status === SESSION_STATUS.BOOTING || isLoadingAdmin) return <BootScreen />;
  if (!isSignedIn) return <SignedOutApp />;
  return <SignedInApp />;
};

export default App;
